import { useState } from "react";
import clsx from "clsx";
import { Button } from "./ui/Button";
import type { CalendarViewName } from "./CalendarView";

interface TopNavProps {
  title: string;
  view: CalendarViewName;
  onChangeView: (view: CalendarViewName) => void;
  onToday: () => void;
  onPrev: () => void;
  onNext: () => void;
  onSearch: (query: string) => void;
  onAddEvent: () => void;
  onImport: () => void;
  onOpenSettings: () => void;
  isAdmin: boolean;
  onSignIn: () => void;
  onSignOut: () => void;
}

const VIEW_OPTIONS: { value: CalendarViewName; label: string }[] = [
  { value: "dayGridMonth", label: "Month" },
  { value: "timeGridWeek", label: "Week" },
  { value: "timeGridDay", label: "Day" },
  { value: "listMonth", label: "Agenda" },
];

export function TopNav({
  title,
  view,
  onChangeView,
  onToday,
  onPrev,
  onNext,
  onSearch,
  onAddEvent,
  onImport,
  onOpenSettings,
  isAdmin,
  onSignIn,
  onSignOut,
}: TopNavProps) {
  const [searchValue, setSearchValue] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-30 hidden border-b border-line dark:border-line-dark bg-surface/90 dark:bg-surface-dark/90 backdrop-blur sm:block">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="text-xl">📅</span>
            <h1 className="text-base font-semibold tracking-tight text-ink dark:text-ink-dark">
              Family Calendar
            </h1>
          </div>

          <div className="flex flex-1 items-center justify-center gap-2 sm:flex-none">
            <Button variant="secondary" size="sm" onClick={onToday}>
              Today
            </Button>
            <div className="flex items-center rounded-lg border border-line dark:border-line-dark">
              <button
                aria-label="Previous"
                onClick={onPrev}
                className="px-2.5 py-1.5 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark"
              >
                ‹
              </button>
              <button
                aria-label="Next"
                onClick={onNext}
                className="border-l border-line dark:border-line-dark px-2.5 py-1.5 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark"
              >
                ›
              </button>
            </div>
            <span className="hidden min-w-[10rem] text-sm font-medium text-ink dark:text-ink-dark sm:block">
              {title}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {isAdmin ? (
              <>
                <Button size="sm" variant="secondary" onClick={onImport} className="hidden md:inline-flex">
                  Import schedule
                </Button>
                <Button size="sm" variant="secondary" onClick={onOpenSettings} className="hidden md:inline-flex">
                  Settings
                </Button>
                <Button size="sm" variant="primary" onClick={onAddEvent}>
                  + Add event
                </Button>
                <div className="relative">
                  <button
                    onClick={() => setMenuOpen((v) => !v)}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-ink/10 dark:bg-white/10 text-sm font-semibold md:hidden"
                    aria-label="More actions"
                  >
                    ⋯
                  </button>
                  {menuOpen && (
                    <div className="absolute right-0 top-10 z-40 w-44 rounded-xl border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-1 shadow-popover md:hidden">
                      <button
                        onClick={() => { setMenuOpen(false); onImport(); }}
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-canvas dark:hover:bg-canvas-dark"
                      >
                        Import schedule
                      </button>
                      <button
                        onClick={() => { setMenuOpen(false); onOpenSettings(); }}
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-canvas dark:hover:bg-canvas-dark"
                      >
                        Settings
                      </button>
                      <button
                        onClick={() => { setMenuOpen(false); onSignOut(); }}
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-canvas dark:hover:bg-canvas-dark"
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
                <Button size="sm" variant="ghost" onClick={onSignOut} className="hidden md:inline-flex">
                  Sign out
                </Button>
              </>
            ) : (
              <Button size="sm" variant="secondary" onClick={onSignIn} className="hidden sm:inline-flex">
                Admin sign in
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="hidden rounded-lg border border-line dark:border-line-dark p-0.5 sm:flex">
            {VIEW_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => onChangeView(opt.value)}
                className={clsx(
                  "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                  view === opt.value
                    ? "bg-ink text-white dark:bg-white dark:text-ink"
                    : "text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark",
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <input
            type="search"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              onSearch(e.target.value);
            }}
            placeholder="Search events…"
            className="w-full max-w-xs rounded-lg border border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark px-3 py-1.5 text-sm placeholder:text-muted dark:placeholder:text-muted-dark focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>
      </div>
    </header>
  );
}
